import React, { useState } from 'react';
import IconPicker from './IconPicker';

const WebsiteDesignsAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const authHeaders = { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' };

  const [websiteDesignSubTab, setWebsiteDesignSubTab] = useState('login');
  const [designLibrary, setDesignLibrary] = useState({ active_design_id: 'classic', custom_designs: [] });
  const [designLibraryLoaded, setDesignLibraryLoaded] = useState(false);
  const [editingDesign, setEditingDesign] = useState(null);
  const [navLibrary, setNavLibrary] = useState({ active_design_id: 'classic', custom_designs: [], menu_order: [], menu_items: {}, submenu_items: {}, nav_icon_size: '', nav_icon_size_collapsed: '' });
  const [navLibraryLoaded, setNavLibraryLoaded] = useState(false);
  const [editingNavDesign, setEditingNavDesign] = useState(null);
  const [editingMenuItem, setEditingMenuItem] = useState(null);
  const [selectedNavDesignId, setSelectedNavDesignId] = useState('classic');
  const [expandedSubmenuParent, setExpandedSubmenuParent] = useState(null);
  const [editingSubMenuItem, setEditingSubMenuItem] = useState(null);
  const [iconPickerTarget, setIconPickerTarget] = useState(null); // { type: 'menu'|'submenu', itemId, parentId? }

  // Image picker state (for login design background)
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [pickerImages, setPickerImages] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerTab, setPickerTab] = useState('icons');
  const [pickerFolders, setPickerFolders] = useState([]);
  const [pickerSelectedFolder, setPickerSelectedFolder] = useState(null);

  const loadPickerImages = async (tab, folderId) => {
    setPickerLoading(true);
    setPickerImages([]);
    try {
      let url;
      if (tab === 'icons') url = `${API}/admin/icons`;
      else if (tab === 'avatars') url = `${API}/admin/avatar-images`;
      else if (tab === 'folders' && folderId) url = `${API}/admin/image-assets/${folderId}`;
      else if (tab === 'folders') {
        const res = await fetch(`${API}/admin/image-folders`, { headers: { 'Authorization': `Bearer ${adminToken}` } });
        if (res.ok) {
          const data = await res.json();
          setPickerFolders(data.folders || []);
        }
        setPickerLoading(false);
        return;
      }
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${adminToken}` } });
      if (res.ok) {
        const data = await res.json();
        setPickerImages(data.icons || data.avatar_images || data.assets || []);
      }
    } catch (e) { console.error('Error loading picker images:', e); }
    setPickerLoading(false);
  };

  const openImagePicker = () => {
    setShowImagePicker(true);
    setPickerTab('icons');
    setPickerSelectedFolder(null);
    loadPickerImages('icons');
  };

  const openIconPicker = (target) => {
    setIconPickerTarget(target);
  };

  const selectIcon = (iconUrl) => {
    if (iconPickerTarget?.type === 'menu') {
      const itemId = iconPickerTarget.itemId;
      setNavLibrary(prev => ({
        ...prev,
        menu_items: {
          ...prev.menu_items,
          [itemId]: { ...prev.menu_items[itemId], icon_url: iconUrl }
        }
      }));
    } else if (iconPickerTarget?.type === 'submenu') {
      const { parentId, itemId } = iconPickerTarget;
      setNavLibrary(prev => ({
        ...prev,
        submenu_items: {
          ...prev.submenu_items,
          [parentId]: {
            ...prev.submenu_items[parentId],
            items: {
              ...prev.submenu_items[parentId]?.items,
              [itemId]: { ...prev.submenu_items[parentId]?.items?.[itemId], icon_url: iconUrl }
            }
          }
        }
      }));
    }
    setIconPickerTarget(null);
  };

  const clearIconUrl = (type, itemId, parentId) => {
    if (type === 'menu') {
      const updated = { ...navLibrary.menu_items[itemId] };
      delete updated.icon_url;
      setNavLibrary(prev => ({ ...prev, menu_items: { ...prev.menu_items, [itemId]: updated } }));
    } else if (type === 'submenu') {
      const subItems = { ...navLibrary.submenu_items[parentId]?.items };
      const updated = { ...subItems[itemId] };
      delete updated.icon_url;
      subItems[itemId] = updated;
      setNavLibrary(prev => ({
        ...prev,
        submenu_items: { ...prev.submenu_items, [parentId]: { ...prev.submenu_items[parentId], items: subItems } }
      }));
    }
  };

  return (
                <div className="space-y-6">
                  {/* Sub-tabs */}
                  <div className={`flex gap-2 flex-wrap`}>
                    {[
                      { key: 'login', label: 'Login Page', icon: '🔐' },
                      { key: 'navigation', label: 'Navigation Menu', icon: '📱' },
                    ].map(tab => (
                      <button key={tab.key} onClick={() => {
                        setWebsiteDesignSubTab(tab.key);
                      }} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        websiteDesignSubTab === tab.key
                          ? 'bg-blue-600 text-white'
                          : isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'
                      }`}>
                        <span>{tab.icon}</span> {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Login Page Design */}
                  {websiteDesignSubTab === 'login' && (() => {
                    // Load design library on first render
                    if (!designLibraryLoaded) {
                      fetch(`${API}/admin/website-designs/login`, { headers: authHeaders })
                        .then(r => r.json())
                        .then(data => {
                          setDesignLibrary({
                            active_design_id: data.active_design_id || 'classic',
                            custom_designs: data.custom_designs || [],
                          });
                          setDesignLibraryLoaded(true);
                        })
                        .catch(() => setDesignLibraryLoaded(true));
                    }

                    const saveDesignLibrary = async (updates) => {
                      const newLib = { ...designLibrary, ...updates };
                      setDesignLibrary(newLib);
                      try {
                        await fetch(`${API}/admin/website-designs/login`, {
                          method: 'PUT', headers: {...authHeaders, 'Content-Type': 'application/json'},
                          body: JSON.stringify(updates)
                        });
                        showNotification('Design saved', 'success');
                        window.dispatchEvent(new Event('loginDesignChanged'));
                      } catch (e) { showNotification('Failed to save', 'error'); }
                    };

                    const activeDesign = designLibrary.custom_designs.find(d => d.id === designLibrary.active_design_id);
                    const activeLabel = designLibrary.active_design_id === 'classic' ? 'Classic' : (activeDesign?.name || 'Classic');

                    const getDesignBgStyle = (d) => {
                      if (!d) return { background: 'linear-gradient(135deg, #0f172a, #1e1b4b)' };
                      if (d.bg_type === 'image' && d.bg_image_url) return { backgroundImage: `url(${d.bg_image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' };
                      return { background: d.bg_gradient || 'linear-gradient(135deg, #0f172a, #1e1b4b)' };
                    };

                    return (
                      <div className="space-y-6">
                        {/* Design Library Header */}
                        <div className={`p-6 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                          <div className="flex items-center justify-between mb-4">
                            <h3 className={`font-semibold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                              🎨 Login Page Designs
                            </h3>
                            <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              Active: <span className="font-medium text-green-500">{activeLabel}</span>
                            </span>
                          </div>

                          {/* Design Cards Grid */}
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                            {/* Classic Design Card (always first, not editable) */}
                            <div className="relative">
                              <button
                                onClick={() => saveDesignLibrary({ active_design_id: 'classic' })}
                                className={`w-full p-3 rounded-xl border-2 transition-all text-left ${
                                  designLibrary.active_design_id === 'classic'
                                    ? 'border-green-500 ring-2 ring-green-500/20'
                                    : isDarkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'
                                }`}
                              >
                                {designLibrary.active_design_id === 'classic' && (
                                  <div className="absolute top-2 right-2 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
                                  </div>
                                )}
                                <div className="h-20 rounded-lg mb-2 flex items-center justify-center" style={{ background: isDarkMode ? '#111827' : 'linear-gradient(135deg, #eff6ff, #fff, #fff7ed)' }}>
                                  <div className="w-14 h-10 rounded-lg shadow-sm" style={{ background: isDarkMode ? '#1f2937' : '#fff', border: `1px solid ${isDarkMode ? '#374151' : '#e5e7eb'}` }} />
                                </div>
                                <p className={`font-medium text-xs text-center ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Classic</p>
                              </button>
                            </div>

                            {/* Custom Design Cards */}
                            {designLibrary.custom_designs.map(d => (
                              <div key={d.id} className="relative group">
                                <button
                                  onClick={() => saveDesignLibrary({ active_design_id: d.id })}
                                  className={`w-full p-3 rounded-xl border-2 transition-all text-left ${
                                    designLibrary.active_design_id === d.id
                                      ? 'border-green-500 ring-2 ring-green-500/20'
                                      : isDarkMode ? 'border-gray-600 hover:border-gray-500' : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                >
                                  {designLibrary.active_design_id === d.id && (
                                    <div className="absolute top-2 right-2 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center z-10">
                                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
                                    </div>
                                  )}
                                  <div className="h-20 rounded-lg mb-2 overflow-hidden" style={getDesignBgStyle(d)}>
                                    <div className="h-full flex items-center justify-center">
                                      <div className="w-14 h-10 rounded-lg" style={{ background: `rgba(255,255,255,${(d.glass_opacity || 10) / 100})`, backdropFilter: `blur(${d.glass_blur || 20}px)`, border: '1px solid rgba(255,255,255,0.2)' }} />
                                    </div>
                                  </div>
                                  <p className={`font-medium text-xs text-center ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                    {d.icon || '🎨'} {d.name}
                                  </p>
                                </button>
                                {/* Action buttons on hover */}
                                <div className="absolute top-1 left-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                                  <button
                                    onClick={(e) => { e.stopPropagation(); setEditingDesign({...d}); }}
                                    className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-blue-600 shadow"
                                    title="Edit"
                                  >&#9998;</button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const copy = {...d, id: 'design-' + Date.now(), name: d.name + ' (copy)'};
                                      const updated = [...designLibrary.custom_designs, copy];
                                      saveDesignLibrary({ custom_designs: updated });
                                      setEditingDesign(copy);
                                    }}
                                    className="w-6 h-6 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-purple-600 shadow"
                                    title="Duplicate"
                                  >&#9112;</button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setEditingDesign({ _deleteConfirm: true, _deleteTarget: d });
                                    }}
                                    className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600 shadow"
                                    title="Delete"
                                  >&times;</button>
                                </div>
                              </div>
                            ))}

                            {/* Create New Design Button */}
                            <button
                              onClick={() => setEditingDesign({
                                id: 'design-' + Date.now(),
                                name: '', icon: '🎨',
                                bg_type: 'gradient', bg_gradient: 'linear-gradient(135deg, #0f172a, #1e1b4b)', bg_image_url: '',
                                welcome_text: '', welcome_text_color: '#ffffff',
                                glass_blur: 20, glass_opacity: 10, image_blur: 0, text_shadow: false,
                                page_text_color: '#ffffff', feature_icon_color: 'rgba(255,255,255,0.4)', footer_color: 'rgba(255,255,255,0.3)',
                                lang_text_color: '#9ca3af', lang_active_bg: '#2563eb',
                                tab_bg: 'rgba(255,255,255,0.08)', tab_active_bg: 'rgba(255,255,255,0.15)', tab_active_text: '#ffffff', tab_inactive_text: 'rgba(255,255,255,0.5)',
                                card_bg: 'rgba(255,255,255,0.1)', card_border: 'rgba(255,255,255,0.15)',
                                label_color: '#d1d5db', icon_color: '#6b7280',
                                input_bg: 'rgba(55,65,81,0.5)', input_border: '#4b5563', input_text: '#ffffff', input_placeholder: '#6b7280',
                                secondary_text_color: '#9ca3af', link_color: '#2563eb',
                                button_bg: '#2563eb', button_text: '#ffffff',
                                _isNew: true,
                              })}
                              className={`w-full p-3 rounded-xl border-2 border-dashed transition-all flex flex-col items-center justify-center min-h-[120px] ${
                                isDarkMode ? 'border-gray-600 hover:border-blue-500 text-gray-400 hover:text-blue-400' : 'border-gray-300 hover:border-blue-500 text-gray-400 hover:text-blue-500'
                              }`}
                            >
                              <span className="text-2xl mb-1">+</span>
                              <span className="text-xs font-medium">New Design</span>
                            </button>
                          </div>
                        </div>

                        {/* Delete Confirmation */}
                        {editingDesign?._deleteConfirm && (
                          <div className={`p-6 rounded-lg border-2 ${isDarkMode ? 'border-red-500/50 bg-gray-800' : 'border-red-500/50 bg-white'}`}>
                            <div className="flex items-start gap-4">
                              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                                <span className="text-2xl">🗑️</span>
                              </div>
                              <div className="flex-1">
                                <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Delete Design</h3>
                                <p className={`mt-1 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                  Are you sure you want to delete <strong>"{editingDesign._deleteTarget?.icon} {editingDesign._deleteTarget?.name}"</strong>? This cannot be undone.
                                </p>
                                <div className="flex gap-3 mt-4">
                                  <button
                                    onClick={() => {
                                      const d = editingDesign._deleteTarget;
                                      if (d.bg_image_url) {
                                        fetch(`${API}/admin/website-designs/login/delete-image`, {
                                          method: 'DELETE', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                          body: JSON.stringify({ url: d.bg_image_url })
                                        }).catch(() => {});
                                      }
                                      const updated = designLibrary.custom_designs.filter(x => x.id !== d.id);
                                      const changes = { custom_designs: updated };
                                      if (designLibrary.active_design_id === d.id) changes.active_design_id = 'classic';
                                      saveDesignLibrary(changes);
                                      setEditingDesign(null);
                                    }}
                                    className="px-5 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700"
                                  >Yes, Delete</button>
                                  <button
                                    onClick={() => setEditingDesign(null)}
                                    className={`px-5 py-2 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                  >Cancel</button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Edit / Create Panel */}
                        {editingDesign && !editingDesign._deleteConfirm && (
                          <div className={`p-6 rounded-lg border-2 ${isDarkMode ? 'border-blue-500/50 bg-gray-800' : 'border-blue-500/50 bg-white'}`}>
                            <h3 className={`font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                              {editingDesign._isNew ? '+ Create New Design' : `Edit: ${editingDesign.icon} ${editingDesign.name}`}
                            </h3>
                            <div className="space-y-4">
                              {/* Name + Icon */}
                              <div className="grid grid-cols-3 gap-3">
                                <div className="col-span-2">
                                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Design Name</label>
                                  <input
                                    type="text" value={editingDesign.name}
                                    onChange={(e) => setEditingDesign(prev => ({...prev, name: e.target.value}))}
                                    placeholder="e.g. Ramadan, Summer Vibes..."
                                    className={`w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 placeholder-gray-400'}`}
                                  />
                                </div>
                                <div>
                                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Icon</label>
                                  <input
                                    type="text" value={editingDesign.icon}
                                    onChange={(e) => setEditingDesign(prev => ({...prev, icon: e.target.value}))}
                                    placeholder="🎨"
                                    className={`w-full px-3 py-2 rounded-lg border text-sm text-center ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                                  />
                                </div>
                              </div>

                              {/* Background Type Toggle */}
                              <div>
                                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Background</label>
                                <div className="flex gap-2 mb-3">
                                  <button
                                    onClick={() => setEditingDesign(prev => ({...prev, bg_type: 'gradient'}))}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                      editingDesign.bg_type === 'gradient' ? 'bg-blue-600 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                                    }`}
                                  >CSS Gradient</button>
                                  <button
                                    onClick={() => setEditingDesign(prev => ({...prev, bg_type: 'image'}))}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                      editingDesign.bg_type === 'image' ? 'bg-blue-600 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                                    }`}
                                  >Image</button>
                                </div>

                                {editingDesign.bg_type === 'gradient' ? (
                                  <div className="space-y-2">
                                    <input
                                      type="text" value={editingDesign.bg_gradient}
                                      onChange={(e) => setEditingDesign(prev => ({...prev, bg_gradient: e.target.value}))}
                                      placeholder="linear-gradient(135deg, #0f172a, #1e1b4b)"
                                      className={`w-full px-3 py-2 rounded-lg border text-sm font-mono ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 placeholder-gray-400'}`}
                                    />
                                    <div className="h-16 rounded-lg border overflow-hidden" style={{
                                      background: (() => {
                                        const val = (editingDesign.bg_gradient || '').trim();
                                        const m = val.match(/(?:linear|radial|conic)-gradient\([^)]+\)/i);
                                        return m ? m[0] : (isDarkMode ? '#374151' : '#f3f4f6');
                                      })()
                                    }} />
                                    <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                                      Paste any CSS gradient. Example: linear-gradient(to right, #4facfe 0%, #00f2fe 100%)
                                    </p>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    <button
                                      type="button"
                                      onClick={openImagePicker}
                                      className="px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded-lg hover:bg-purple-700 whitespace-nowrap"
                                    >
                                      Pick from Library
                                    </button>
                                    {editingDesign.bg_image_url && (
                                      <div className="relative">
                                        <div className="h-24 rounded-lg overflow-hidden border" style={{
                                          backgroundImage: `url(${editingDesign.bg_image_url})`, backgroundSize: 'cover', backgroundPosition: 'center',
                                          borderColor: isDarkMode ? '#374151' : '#e5e7eb'
                                        }} />
                                        <button
                                          onClick={async () => {
                                            try {
                                              await fetch(`${API}/admin/website-designs/login/delete-image`, {
                                                method: 'DELETE', headers: {...authHeaders, 'Content-Type': 'application/json'},
                                                body: JSON.stringify({ url: editingDesign.bg_image_url })
                                              });
                                              setEditingDesign(prev => ({...prev, bg_image_url: ''}));
                                              showNotification('Image deleted', 'success');
                                            } catch (e) { showNotification('Failed to delete', 'error'); }
                                          }}
                                          className="absolute top-1 right-1 px-2 py-1 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 shadow"
                                        >Delete Image</button>
                                      </div>
                                    )}
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="text"
                                        placeholder="Or paste image URL here..."
                                        value={editingDesign.bg_image_url || ''}
                                        onChange={(e) => setEditingDesign(prev => ({...prev, bg_image_url: e.target.value}))}
                                        className={`flex-1 px-2 py-1.5 text-xs rounded border ${isDarkMode ? 'bg-gray-800 text-white border-gray-600' : 'bg-white text-gray-800 border-gray-300'}`}
                                      />
                                      {editingDesign.bg_image_url && (
                                        <button
                                          type="button"
                                          onClick={() => setEditingDesign(prev => ({...prev, bg_image_url: ''}))}
                                          className="px-2 py-1.5 bg-gray-500 text-white text-xs rounded hover:bg-gray-600"
                                        >Clear</button>
                                      )}
                                    </div>
                                    <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Pick from library or paste any image URL.</p>
                                  </div>
                                )}
                              </div>

                              {/* Welcome Note */}
                              <div className="grid grid-cols-3 gap-3">
                                <div className="col-span-2">
                                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Welcome Note (above logo)</label>
                                  <input
                                    type="text" value={editingDesign.welcome_text}
                                    onChange={(e) => setEditingDesign(prev => ({...prev, welcome_text: e.target.value}))}
                                    placeholder="Leave empty to hide"
                                    className={`w-full px-3 py-2 rounded-lg border text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'bg-white border-gray-300 placeholder-gray-400'}`}
                                  />
                                </div>
                                <div>
                                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Text Color</label>
                                  <div className="flex items-center gap-2">
                                    <input type="color" value={editingDesign.welcome_text_color}
                                      onChange={(e) => setEditingDesign(prev => ({...prev, welcome_text_color: e.target.value}))}
                                      className="w-10 h-10 rounded cursor-pointer border-0" />
                                    <input type="text" value={editingDesign.welcome_text_color}
                                      onChange={(e) => setEditingDesign(prev => ({...prev, welcome_text_color: e.target.value}))}
                                      className={`w-20 px-2 py-2 rounded-lg border text-xs font-mono ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`} />
                                  </div>
                                </div>
                              </div>

                              {/* Glass & Blur Controls */}
                              <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                Glass Blur = frosted effect on the login card. Glass Opacity = how see-through the card is. Image Blur = blur the background image itself.
                              </p>
                              <div className="grid grid-cols-3 gap-4">
                                <div>
                                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Glass Blur (Card): {editingDesign.glass_blur ?? 20}px
                                  </label>
                                  <input type="range" min="0" max="40" value={editingDesign.glass_blur ?? 20}
                                    onChange={(e) => setEditingDesign(prev => ({...prev, glass_blur: parseInt(e.target.value)}))}
                                    className="w-full" />
                                </div>
                                <div>
                                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Glass Opacity (Card): {editingDesign.glass_opacity ?? 10}%
                                  </label>
                                  <input type="range" min="0" max="30" value={editingDesign.glass_opacity ?? 10}
                                    onChange={(e) => setEditingDesign(prev => ({...prev, glass_opacity: parseInt(e.target.value)}))}
                                    className="w-full" />
                                </div>
                                <div>
                                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                    Image Blur (Background): {editingDesign.image_blur ?? 0}px
                                  </label>
                                  <input type="range" min="0" max="20" value={editingDesign.image_blur ?? 0}
                                    onChange={(e) => setEditingDesign(prev => ({...prev, image_blur: parseInt(e.target.value)}))}
                                    className="w-full" />
                                </div>
                              </div>

                              {/* Text Shadow Toggle */}
                              <div className="flex items-center gap-3">
                                <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Text Shadow (readability on bright backgrounds)</label>
                                <button
                                  onClick={() => setEditingDesign(prev => ({...prev, text_shadow: !prev.text_shadow}))}
                                  className={`w-12 h-6 rounded-full transition-colors relative ${editingDesign.text_shadow ? 'bg-blue-600' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}
                                >
                                  <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${editingDesign.text_shadow ? 'translate-x-6' : 'translate-x-0.5'}`} />
                                </button>
                              </div>

                              {/* ═══ COLOR CONTROLS ═══ */}
                              {(() => {
                                const hexToRgba = (color, opacity) => {
                                  if (!color || color.startsWith('rgba')) return color;
                                  const hex = color.replace('#', '');
                                  if (hex.length < 6) return color;
                                  const r = parseInt(hex.substring(0, 2), 16);
                                  const g = parseInt(hex.substring(2, 4), 16);
                                  const b = parseInt(hex.substring(4, 6), 16);
                                  return `rgba(${r},${g},${b},${opacity})`;
                                };
                                const ColorPicker = ({ label, field, defaultVal, example, isBg }) => {
                                  const currentColor = editingDesign[field] || defaultVal || '#ffffff';
                                  return (
                                  <div className={`p-2.5 rounded-lg border ${isDarkMode ? 'border-gray-700 bg-gray-750' : 'border-gray-200 bg-gray-50'}`}>
                                    <div className="flex items-center gap-2 mb-2">
                                      <div className="w-5 h-5 rounded-full border border-gray-500 flex-shrink-0" style={{ background: currentColor }} />
                                      <span className={`text-xs font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{label}</span>
                                    </div>
                                    <div className="mb-2 rounded overflow-hidden" style={{ background: 'linear-gradient(to right, #111827 50%, #f3f4f6 50%)' }}>
                                      <div className="px-2 py-1.5 text-center">
                                        {isBg === 'border' ? (
                                          <div className="h-5 rounded" style={{ border: `2px solid ${currentColor}`, background: 'transparent' }} />
                                        ) : isBg ? (
                                          <div className="h-5 rounded" style={{ background: currentColor }} />
                                        ) : (
                                          <span className="text-sm font-medium" style={{ color: currentColor }}>{example}</span>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                      <input type="color" value={currentColor.replace(/rgba?\([^)]+\)/, '#ffffff')}
                                        onChange={(e) => setEditingDesign(prev => ({...prev, [field]: e.target.value}))}
                                        className="w-7 h-7 rounded cursor-pointer border-0 p-0" />
                                      <input type="text" value={currentColor}
                                        onChange={(e) => setEditingDesign(prev => ({...prev, [field]: e.target.value}))}
                                        className={`flex-1 px-2 py-1 rounded border text-xs font-mono ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'}`}
                                        placeholder={defaultVal} />
                                    </div>
                                  </div>
                                  );
                                };
                                return (
                                  <div className="space-y-4">
                                    <h4 className={`text-sm font-semibold border-t pt-4 ${isDarkMode ? 'text-gray-200 border-gray-700' : 'text-gray-700 border-gray-200'}`}>Page Colors</h4>
                                    <div className="grid grid-cols-3 gap-3">
                                      <ColorPicker label="Page Text (Learn Moroccan Darija)" field="page_text_color" defaultVal="#ffffff" example="Learn Moroccan Darija" />
                                      <ColorPicker label="Feature Icons (Translate Games)" field="feature_icon_color" defaultVal="rgba(255,255,255,0.4)" example="Translate  Games  Dictionary" />
                                      <ColorPicker label="Footer (DarijaPro 2026)" field="footer_color" defaultVal="rgba(255,255,255,0.3)" example="DarijaPro 2026" />
                                    </div>

                                    <h4 className={`text-sm font-semibold border-t pt-4 ${isDarkMode ? 'text-gray-200 border-gray-700' : 'text-gray-700 border-gray-200'}`}>Language Switcher</h4>
                                    <div className="grid grid-cols-2 gap-3">
                                      <ColorPicker label="Inactive Languages (EN FR AR)" field="lang_text_color" defaultVal="#9ca3af" example="EN  FR  AR" />
                                      <ColorPicker label="Active Language BG" field="lang_active_bg" defaultVal="#2563eb" isBg={true} />
                                    </div>

                                    <h4 className={`text-sm font-semibold border-t pt-4 ${isDarkMode ? 'text-gray-200 border-gray-700' : 'text-gray-700 border-gray-200'}`}>Tabs</h4>
                                    <div className="grid grid-cols-2 gap-3">
                                      <ColorPicker label="Tab Background" field="tab_bg" defaultVal="rgba(255,255,255,0.08)" isBg={true} />
                                      <ColorPicker label="Active Tab BG" field="tab_active_bg" defaultVal="rgba(255,255,255,0.15)" isBg={true} />
                                      <ColorPicker label="Active Tab Text (Sign In)" field="tab_active_text" defaultVal="#ffffff" example="Sign In" />
                                      <ColorPicker label="Inactive Tab Text (Create Account)" field="tab_inactive_text" defaultVal="rgba(255,255,255,0.5)" example="Create Account" />
                                    </div>

                                    <h4 className={`text-sm font-semibold border-t pt-4 ${isDarkMode ? 'text-gray-200 border-gray-700' : 'text-gray-700 border-gray-200'}`}>Glass Card</h4>
                                    <div className="grid grid-cols-2 gap-3">
                                      <ColorPicker label="Card Background (Glass Tint)" field="card_bg" defaultVal="rgba(255,255,255,0.1)" isBg={true} />
                                      <ColorPicker label="Card Border" field="card_border" defaultVal="rgba(255,255,255,0.15)" isBg="border" />
                                    </div>

                                    <h4 className={`text-sm font-semibold border-t pt-4 ${isDarkMode ? 'text-gray-200 border-gray-700' : 'text-gray-700 border-gray-200'}`}>Form</h4>
                                    <div className="grid grid-cols-3 gap-3">
                                      <ColorPicker label="Labels (Email, Password)" field="label_color" defaultVal="#d1d5db" example="Email" />
                                      <ColorPicker label="Input Icons (@)" field="icon_color" defaultVal="#6b7280" example="@" />
                                      <ColorPicker label="Input Background" field="input_bg" defaultVal="rgba(55,65,81,0.5)" isBg={true} />
                                      <ColorPicker label="Input Border" field="input_border" defaultVal="#4b5563" isBg="border" />
                                      <ColorPicker label="Input Text (user@email.com)" field="input_text" defaultVal="#ffffff" example="user@email.com" />
                                      <ColorPicker label="Placeholder (you@example.com)" field="input_placeholder" defaultVal="#6b7280" example="you@example.com" />
                                    </div>

                                    <h4 className={`text-sm font-semibold border-t pt-4 ${isDarkMode ? 'text-gray-200 border-gray-700' : 'text-gray-700 border-gray-200'}`}>Text & Buttons</h4>
                                    <div className="grid grid-cols-2 gap-3">
                                      <ColorPicker label="Secondary Text (Remember me)" field="secondary_text_color" defaultVal="#9ca3af" example="Remember me" />
                                      <ColorPicker label="Links (Forgot password?)" field="link_color" defaultVal="#2563eb" example="Forgot password?" />
                                      <ColorPicker label="Button Background" field="button_bg" defaultVal="#2563eb" isBg={true} />
                                      <ColorPicker label="Button Text (Sign In)" field="button_text" defaultVal="#ffffff" example="Sign In" />
                                    </div>
                                  </div>
                                );
                              })()}

                              {/* Live Preview */}
                              <div>
                                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Preview</label>
                                <div className="rounded-xl overflow-hidden relative" style={{ height: '320px' }}>
                                  <div className="absolute inset-0" style={{ ...getDesignBgStyle(editingDesign), filter: (editingDesign.image_blur ?? 0) > 0 ? `blur(${editingDesign.image_blur}px)` : 'none', transform: (editingDesign.image_blur ?? 0) > 0 ? 'scale(1.1)' : 'none' }} />
                                  <div className="relative h-full flex flex-col items-center justify-center px-6">
                                    {editingDesign.welcome_text && (
                                      <p className="text-sm font-medium mb-3" style={{
                                        color: editingDesign.welcome_text_color,
                                        textShadow: editingDesign.text_shadow ? '0 1px 4px rgba(0,0,0,0.5)' : 'none'
                                      }}>{editingDesign.welcome_text}</p>
                                    )}
                                    <p className="text-xs mb-3" style={{ color: editingDesign.page_text_color || '#ffffff', opacity: 0.6 }}>Learn Darija the fun way</p>
                                    <div className="w-52 rounded-xl p-4" style={{
                                      background: editingDesign.card_bg && !editingDesign.card_bg.startsWith('rgba')
                                        ? (() => { const h = editingDesign.card_bg.replace('#',''); return `rgba(${parseInt(h.substring(0,2),16)},${parseInt(h.substring(2,4),16)},${parseInt(h.substring(4,6),16)},${(editingDesign.glass_opacity ?? 10) / 100})`; })()
                                        : editingDesign.card_bg || `rgba(255,255,255,${(editingDesign.glass_opacity ?? 10) / 100})`,
                                      backdropFilter: `blur(${editingDesign.glass_blur ?? 20}px)`,
                                      border: `1px solid ${editingDesign.card_border || 'rgba(255,255,255,0.15)'}`
                                    }}>
                                      <div className="flex gap-1 mb-3">
                                        <div className="flex-1 h-5 rounded text-center text-xs leading-5" style={{ background: editingDesign.tab_active_bg || 'rgba(255,255,255,0.15)', color: editingDesign.tab_active_text || '#fff' }}>Sign In</div>
                                        <div className="flex-1 h-5 rounded text-center text-xs leading-5" style={{ color: editingDesign.tab_inactive_text || 'rgba(255,255,255,0.5)' }}>Sign Up</div>
                                      </div>
                                      <div className="space-y-2">
                                        <p className="text-xs" style={{ color: editingDesign.label_color || '#d1d5db' }}>Email</p>
                                        <div className="h-6 rounded flex items-center px-2" style={{ background: editingDesign.input_bg || 'rgba(55,65,81,0.5)', border: `1px solid ${editingDesign.input_border || '#4b5563'}` }}>
                                          <span className="text-xs" style={{ color: editingDesign.input_placeholder || '#6b7280' }}>you@email.com</span>
                                        </div>
                                        <p className="text-xs" style={{ color: editingDesign.label_color || '#d1d5db' }}>Password</p>
                                        <div className="h-6 rounded" style={{ background: editingDesign.input_bg || 'rgba(55,65,81,0.5)', border: `1px solid ${editingDesign.input_border || '#4b5563'}` }} />
                                        <div className="flex justify-between items-center">
                                          <span className="text-xs" style={{ color: editingDesign.secondary_text_color || '#9ca3af' }}>Remember me</span>
                                          <span className="text-xs" style={{ color: editingDesign.link_color || '#2563eb' }}>Forgot?</span>
                                        </div>
                                        <div className="h-7 rounded mt-2 flex items-center justify-center" style={{ background: editingDesign.button_bg || '#2563eb' }}>
                                          <span className="text-xs font-semibold" style={{ color: editingDesign.button_text || '#fff' }}>Sign In</span>
                                        </div>
                                      </div>
                                    </div>
                                    <p className="text-xs mt-2" style={{ color: editingDesign.footer_color || 'rgba(255,255,255,0.3)' }}>DarijaPro 2026</p>
                                  </div>
                                </div>
                              </div>

                              {/* Save / Cancel */}
                              <div className="flex gap-3">
                                <button
                                  onClick={() => {
                                    if (!editingDesign.name?.trim()) { showNotification('Enter a design name', 'error'); return; }
                                    let css = (editingDesign.bg_gradient || '').trim();
                                    if (editingDesign.bg_type === 'gradient' && css) {
                                      const match = css.match(/(?:linear|radial|conic)-gradient\([^)]+\)/i);
                                      if (match) css = match[0];
                                    }
                                    const design = { ...editingDesign, bg_gradient: css || editingDesign.bg_gradient };
                                    delete design._isNew;
                                    const existing = designLibrary.custom_designs.find(x => x.id === design.id);
                                    const updated = existing
                                      ? designLibrary.custom_designs.map(x => x.id === design.id ? design : x)
                                      : [...designLibrary.custom_designs, design];
                                    saveDesignLibrary({ custom_designs: updated });
                                    setEditingDesign(null);
                                  }}
                                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                                >
                                  {editingDesign._isNew ? 'Create Design' : 'Save Changes'}
                                </button>
                                <button
                                  onClick={() => setEditingDesign(null)}
                                  className={`px-4 py-2.5 rounded-lg text-sm font-medium ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                >Cancel</button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Navigation Menu Design */}
                  {websiteDesignSubTab === 'navigation' && (() => {
                    // Load nav library on first render
                    if (!navLibraryLoaded) {
                      fetch(`${API}/admin/website-designs/navigation`, { headers: authHeaders })
                        .then(r => r.json())
                        .then(data => {
                          setNavLibrary({
                            active_design_id: data.active_design_id || 'classic',
                            custom_designs: data.custom_designs || [],
                            menu_order: data.menu_order || [],
                            menu_items: data.menu_items || {},
                            submenu_items: data.submenu_items || {},
                            nav_icon_size: data.nav_icon_size || '',
                            nav_icon_size_collapsed: data.nav_icon_size_collapsed || '',
                          });
                          setSelectedNavDesignId(data.active_design_id || 'classic');
                          setNavLibraryLoaded(true);
                        })
                        .catch(() => setNavLibraryLoaded(true));
                    }

                    // IMPORTANT: When adding a new navigation tab, add its ID here AND in MENU_ITEM_DEFAULTS below.
                    // Also add it to NAV_LIBRARY_DEFAULTS in server.py. Do NOT hardcode position in ModernLayout.js.
                    const DEFAULT_MENU_ORDER = ['translator','dictionary','decoder','amazigh-translation','my-daily-words','traveltools','music','culturePod','learning','verbExplorer','dashboard','games','holidays','help'];

                    const NAV_TEMPLATE = {
                      bg_type: 'gradient',
                      light_gradient_from: '#2563eb', light_gradient_via: '#1d4ed8', light_gradient_to: '#1e3a8a',
                      dark_gradient_from: '#1f2937', dark_gradient_via: '#1f2937', dark_gradient_to: '#111827',
                      light_solid_bg: '#ffffff', dark_solid_bg: '#1f2937',
                      light_text_color: '#e0e7ff', dark_text_color: '#d1d5db',
                      active_style: 'rounded',
                      active_bg_light: '#ffffff', active_text_light: '#2563eb',
                      active_bg_dark: '#4b5563', active_text_dark: '#ffffff',
                      hover_bg_light: 'rgba(255,255,255,0.1)', hover_bg_dark: 'rgba(107,114,128,0.5)',
                      glass_enabled: false, glass_blur: 10, glass_opacity: 80,
                      sidebar_width: 288,
                    };

                    const MENU_ITEM_DEFAULTS = {
                      translator: { label: 'Translation', icon: '🌐' },
                      dictionary: { label: 'Dictionary', icon: '📖' },
                      decoder: { label: 'Darija Decoder', icon: '🔮' },
                      'amazigh-translation': { label: 'Amazigh', icon: 'ⵣ' },
                      'my-daily-words': { label: 'Daily Words', icon: '📚' },
                      traveltools: { label: 'My Morocco', icon: '🇲🇦' },
                      music: { label: 'Music', icon: '🎵' },
                      culturePod: { label: 'Culture Pod', icon: '🎧' },
                      learning: { label: 'Learn', icon: '📚' },
                      dashboard: { label: 'Dashboard', icon: '📊' },
                      games: { label: 'Games', icon: '🎮' },
                      verbExplorer: { label: 'Verb Explorer', icon: '📖' },
                      holidays: { label: 'Holidays', icon: '🎉' },
                      help: { label: 'Help', icon: '❓' },
                    };

                    const getEffectiveMenuOrder = () => {
                      const saved = navLibrary.menu_order?.length ? navLibrary.menu_order : DEFAULT_MENU_ORDER;
                      const missing = DEFAULT_MENU_ORDER.filter(id => !saved.includes(id));
                      return [...saved, ...missing];
                    };

                    const getMenuItem = (id) => {
                      const override = navLibrary.menu_items[id];
                      const def = MENU_ITEM_DEFAULTS[id] || { label: id, icon: '?' };
                      return { label: override?.label || def.label, icon: override?.icon || def.icon };
                    };

                    // Submenu defaults — mirrors the hardcoded submenus in ModernLayout.js
                    const SUBMENU_DEFAULTS = {
                      dashboard: [
                        { id: 'dashboard-main', label: 'Dashboard', icon: '📊' },
                        { id: 'profile', label: 'Profile', icon: '👤' },
                        { id: 'membership', label: 'Membership', icon: '💎' },
                        { id: 'orders', label: 'Orders', icon: '📦' },
                      ],
                    };

                    const getSubMenuItem = (parentId, subId) => {
                      const override = navLibrary.submenu_items?.[parentId]?.items?.[subId];
                      const defaults = SUBMENU_DEFAULTS[parentId] || [];
                      const def = defaults.find(d => d.id === subId) || { label: subId, icon: '•' };
                      return { label: override?.label || def.label, icon: override?.icon || def.icon };
                    };

                    const getSubmenuOrder = (parentId) => {
                      const saved = navLibrary.submenu_items?.[parentId]?.order;
                      if (saved?.length) return saved;
                      return (SUBMENU_DEFAULTS[parentId] || []).map(d => d.id);
                    };

                    const moveSubMenuItem = (parentId, idx, dir) => {
                      const order = [...getSubmenuOrder(parentId)];
                      const newIdx = idx + dir;
                      if (newIdx < 0 || newIdx >= order.length) return;
                      [order[idx], order[newIdx]] = [order[newIdx], order[idx]];
                      setNavLibrary(prev => ({
                        ...prev,
                        submenu_items: {
                          ...prev.submenu_items,
                          [parentId]: { ...prev.submenu_items?.[parentId], order }
                        }
                      }));
                    };

                    const saveNavLibrary = async (updates) => {
                      const merged = { ...navLibrary, ...updates };
                      try {
                        await fetch(`${API}/admin/website-designs/navigation`, {
                          method: 'PUT', headers: { ...authHeaders, 'Content-Type': 'application/json' },
                          body: JSON.stringify(merged)
                        });
                        setNavLibrary(merged);
                        // Notify App.js to refetch nav design so sidebar updates live
                        window.dispatchEvent(new Event('navDesignChanged'));
                        showNotification('Navigation design saved!', 'success');
                      } catch { showNotification('Failed to save', 'error'); }
                    };

                    const moveMenuItem = (idx, dir) => {
                      const order = [...getEffectiveMenuOrder()];
                      const newIdx = idx + dir;
                      if (newIdx < 0 || newIdx >= order.length) return;
                      [order[idx], order[newIdx]] = [order[newIdx], order[idx]];
                      setNavLibrary(prev => ({ ...prev, menu_order: order }));
                    };

                    const opacityHex = (pct) => {
                      const val = Math.round(((pct ?? 80) / 100) * 255);
                      return val.toString(16).padStart(2, '0');
                    };

                    const getDesignPreviewBg = (d) => {
                      if (d.bg_type === 'solid') return { background: d.light_solid_bg || '#ffffff' };
                      return { background: `linear-gradient(to bottom, ${d.light_gradient_from || '#2563eb'}, ${d.light_gradient_via || '#1d4ed8'}, ${d.light_gradient_to || '#1e3a8a'})` };
                    };

                    if (!navLibraryLoaded) {
                      return <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading navigation designs...</div>;
                    }

                    // EDITING A DESIGN
                    if (editingNavDesign) {
                      const ed = editingNavDesign;
                      const updateEd = (key, val) => setEditingNavDesign(prev => ({ ...prev, [key]: val }));

                      const saveDesign = () => {
                        const designs = [...navLibrary.custom_designs];
                        const idx = designs.findIndex(d => d.id === ed.id);
                        if (idx >= 0) designs[idx] = ed;
                        else designs.push(ed);
                        saveNavLibrary({ custom_designs: designs });
                        setEditingNavDesign(null);
                      };

                      const previewBg = (() => {
                        if (ed.bg_type === 'solid') {
                          if (ed.glass_enabled) return { backdropFilter: `blur(${ed.glass_blur || 10}px)`, background: `${ed.light_solid_bg || '#ffffff'}${opacityHex(ed.glass_opacity)}` };
                          return { background: ed.light_solid_bg || '#ffffff' };
                        }
                        const f = ed.light_gradient_from || '#2563eb', v = ed.light_gradient_via || '#1d4ed8', t = ed.light_gradient_to || '#1e3a8a';
                        if (ed.glass_enabled) return { backdropFilter: `blur(${ed.glass_blur || 10}px)`, background: `linear-gradient(to bottom, ${f}${opacityHex(ed.glass_opacity)}, ${v}${opacityHex(ed.glass_opacity)}, ${t}${opacityHex(ed.glass_opacity)})` };
                        return { background: `linear-gradient(to bottom, ${f}, ${v}, ${t})` };
                      })();

                      return (
                        <div className="space-y-6">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <button onClick={() => setEditingNavDesign(null)} className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}>
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                              </button>
                              <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Edit: {ed.name}</h3>
                            </div>
                            <div className="flex gap-2">
                              <button onClick={() => setEditingNavDesign(null)} className={`px-3 py-1.5 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>Cancel</button>
                              <button onClick={saveDesign} className="px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">Save Design</button>
                            </div>
                          </div>

                          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
                            <div className="xl:col-span-2 space-y-5">
                              {/* Name & Icon */}
                              <div className={`p-5 rounded-xl ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                                <h4 className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Design Identity</h4>
                                <div className="flex gap-3">
                                  <div className="flex-1">
                                    <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Name</label>
                                    <input type="text" value={ed.name || ''} onChange={e => updateEd('name', e.target.value)}
                                      className={`w-full px-3 py-2 rounded-lg text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'} border`} />
                                  </div>
                                  <div className="w-20">
                                    <label className={`block text-xs mb-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Icon</label>
                                    <input type="text" value={ed.icon || ''} onChange={e => updateEd('icon', e.target.value)}
                                      className={`w-full px-3 py-2 rounded-lg text-sm text-center ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-gray-50 border-gray-200'} border`} />
                                  </div>
                                </div>
                              </div>

                              {/* Background Type */}
                              <div className={`p-5 rounded-xl ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                                <h4 className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Background</h4>
                                <div className="flex gap-2 mb-4">
                                  {['gradient', 'solid'].map(type => (
                                    <button key={type} onClick={() => updateEd('bg_type', type)}
                                      className={`px-4 py-1.5 rounded-lg text-sm font-medium ${ed.bg_type === type ? 'bg-blue-600 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                                      {type === 'gradient' ? 'Gradient' : 'Solid Color'}
                                    </button>
                                  ))}
                                </div>

                                {ed.bg_type === 'gradient' ? (
                                  <div className="space-y-3">
                                    <div>
                                      <label className={`block text-xs mb-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Gradient Colors</label>
                                      <div className="flex items-center gap-2">
                                        {[['light_gradient_from', 'From'], ['light_gradient_via', 'Via'], ['light_gradient_to', 'To']].map(([key, lbl]) => (
                                          <div key={key} className="flex items-center gap-1">
                                            <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{lbl}</span>
                                            <input type="color" value={ed[key] || '#2563eb'} onChange={e => updateEd(key, e.target.value)} className="w-8 h-7 rounded cursor-pointer border-0" />
                                          </div>
                                        ))}
                                        <div className="flex-1 h-5 rounded" style={{ background: `linear-gradient(to right, ${ed.light_gradient_from || '#2563eb'}, ${ed.light_gradient_via || '#1d4ed8'}, ${ed.light_gradient_to || '#1e3a8a'})` }}></div>
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Background</span>
                                    <input type="color" value={ed.light_solid_bg || '#ffffff'} onChange={e => updateEd('light_solid_bg', e.target.value)} className="w-10 h-8 rounded cursor-pointer border-0" />
                                  </div>
                                )}
                              </div>

                              {/* Text & Active Colors */}
                              <div className={`p-5 rounded-xl ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                                <h4 className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Colors</h4>
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className={`block text-xs mb-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Menu Text</label>
                                    <input type="color" value={ed.light_text_color || '#e0e7ff'} onChange={e => updateEd('light_text_color', e.target.value)} className="w-10 h-8 rounded cursor-pointer border-0" />
                                  </div>
                                  <div>
                                    <label className={`block text-xs mb-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Active Item</label>
                                    <div className="flex gap-2">
                                      <input type="color" value={ed.active_bg_light || '#ffffff'} onChange={e => updateEd('active_bg_light', e.target.value)} className="w-10 h-8 rounded cursor-pointer border-0" />
                                      <input type="color" value={ed.active_text_light || '#2563eb'} onChange={e => updateEd('active_text_light', e.target.value)} className="w-10 h-8 rounded cursor-pointer border-0" />
                                      <span className={`text-[10px] self-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>bg / text</span>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Active Style */}
                              <div className={`p-5 rounded-xl ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                                <h4 className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Active Item Style</h4>
                                <div className="flex gap-2">
                                  {[['rounded', 'Rounded'], ['full-width', 'Full Width'], ['left-border', 'Left Border']].map(([val, lbl]) => (
                                    <button key={val} onClick={() => updateEd('active_style', val)}
                                      className={`px-3 py-1.5 rounded-lg text-sm font-medium ${ed.active_style === val ? 'bg-blue-600 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                                      {lbl}
                                    </button>
                                  ))}
                                </div>
                              </div>

                              {/* Glass Effect */}
                              <div className={`p-5 rounded-xl ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                                <h4 className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Glass Effect</h4>
                                <div className="space-y-3">
                                  <div className="flex items-center justify-between">
                                    <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Enable</span>
                                    <button onClick={() => updateEd('glass_enabled', !ed.glass_enabled)}
                                      className={`relative w-11 h-6 rounded-full transition-colors ${ed.glass_enabled ? 'bg-blue-600' : isDarkMode ? 'bg-gray-600' : 'bg-gray-300'}`}>
                                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${ed.glass_enabled ? 'translate-x-5' : 'translate-x-0.5'}`}></div>
                                    </button>
                                  </div>
                                  {ed.glass_enabled && (
                                    <>
                                      <div>
                                        <div className="flex justify-between text-xs mb-1"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Blur</span><span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{ed.glass_blur ?? 10}px</span></div>
                                        <input type="range" min="0" max="30" value={ed.glass_blur ?? 10} onChange={e => updateEd('glass_blur', parseInt(e.target.value))} className="w-full h-1.5 rounded-lg appearance-none cursor-pointer bg-gray-300" />
                                      </div>
                                      <div>
                                        <div className="flex justify-between text-xs mb-1"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Opacity</span><span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{ed.glass_opacity ?? 80}%</span></div>
                                        <input type="range" min="10" max="100" value={ed.glass_opacity ?? 80} onChange={e => updateEd('glass_opacity', parseInt(e.target.value))} className="w-full h-1.5 rounded-lg appearance-none cursor-pointer bg-gray-300" />
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>

                              {/* Width */}
                              <div className={`p-5 rounded-xl ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                                <h4 className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Sidebar Width</h4>
                                <div className="flex justify-between text-xs mb-1"><span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Expanded</span><span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{ed.sidebar_width ?? 288}px</span></div>
                                <input type="range" min="220" max="400" value={ed.sidebar_width ?? 288} onChange={e => updateEd('sidebar_width', parseInt(e.target.value))} className="w-full h-1.5 rounded-lg appearance-none cursor-pointer bg-gray-300" />
                              </div>
                            </div>

                            {/* Live Preview */}
                            <div className="xl:col-span-1">
                              <div className={`sticky top-4 p-4 rounded-xl ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                                <h4 className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Preview</h4>
                                <div className="rounded-xl overflow-hidden shadow-lg mx-auto" style={{ width: 200 }}>
                                  <div className="p-3 text-white" style={{ ...previewBg, minHeight: 300, color: ed.light_text_color || '#e0e7ff' }}>
                                    <div style={{ color: '#fff' }} className="text-xs font-bold mb-0.5 opacity-90">Darija Pro</div>
                                    <div className="text-[9px] mb-3 opacity-50">Language Portal</div>
                                    <div className="space-y-0.5">
                                      {getEffectiveMenuOrder().slice(0, 7).map((itemId, idx) => {
                                        const info = getMenuItem(itemId);
                                        const isAct = idx === 0;
                                        const activeStyle = ed.active_style || 'rounded';
                                        return (
                                          <div key={itemId} className={`flex items-center gap-1.5 px-2 py-1 text-[10px] ${activeStyle === 'full-width' ? 'rounded-md' : 'rounded-lg'} ${isAct && activeStyle === 'left-border' ? 'border-l-2' : ''}`}
                                            style={isAct ? { backgroundColor: ed.active_bg_light || '#ffffff', color: ed.active_text_light || '#2563eb', fontWeight: 600, borderColor: ed.active_text_light || '#2563eb' } : {}}>
                                            <span className="text-xs">{info.icon}</span>
                                            <span>{info.label}</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                </div>
                                <p className={`text-[10px] mt-2 text-center ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Light mode preview</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    // MAIN VIEW — Design Cards
                    return (
                      <div className="space-y-6">
                        <div className="flex items-center justify-between">
                          <h3 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Navigation Menu Designs</h3>
                          <button onClick={() => saveNavLibrary({ active_design_id: selectedNavDesignId })}
                            disabled={selectedNavDesignId === navLibrary.active_design_id}
                            className={`px-5 py-2 rounded-lg text-sm font-bold transition-colors ${selectedNavDesignId !== navLibrary.active_design_id ? 'bg-blue-600 text-white hover:bg-blue-700' : isDarkMode ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`}>
                            Save Design
                          </button>
                        </div>

                        {/* Design Cards Grid */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                          {/* Classic Card (built-in) */}
                          <div onClick={() => setSelectedNavDesignId('classic')}
                            className={`relative cursor-pointer rounded-xl overflow-hidden transition-all hover:scale-105 ${selectedNavDesignId === 'classic' ? 'ring-2 ring-blue-500 ring-offset-2' : ''} ${isDarkMode ? 'ring-offset-gray-900' : 'ring-offset-white'}`}>
                            <div className="h-32 p-3 text-white" style={{ background: 'linear-gradient(to bottom, #2563eb, #1d4ed8, #1e3a8a)' }}>
                              <div className="text-xs font-bold opacity-90">Darija Pro</div>
                              <div className="text-[8px] opacity-50 mb-2">Language Portal</div>
                              <div className="space-y-0.5">
                                {['🌐 Translation', '📖 Dictionary', '🔮 Decoder'].map((item, i) => (
                                  <div key={i} className={`text-[8px] px-1.5 py-0.5 rounded ${i === 0 ? 'bg-white text-blue-600 font-bold' : 'text-blue-100'}`}>{item}</div>
                                ))}
                              </div>
                            </div>
                            <div className={`p-2 text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                              <div className="text-sm font-medium">🔵 Classic Blue</div>
                              {navLibrary.active_design_id === 'classic' && <div className="text-[10px] text-blue-500 font-bold">ACTIVE</div>}
                            </div>
                          </div>

                          {/* Custom Design Cards */}
                          {navLibrary.custom_designs.map(design => (
                            <div key={design.id} className="relative group">
                              <div onClick={() => setSelectedNavDesignId(design.id)}
                                className={`cursor-pointer rounded-xl overflow-hidden transition-all hover:scale-105 ${selectedNavDesignId === design.id ? 'ring-2 ring-blue-500 ring-offset-2' : ''} ${isDarkMode ? 'ring-offset-gray-900' : 'ring-offset-white'}`}>
                                <div className="h-32 p-3" style={{ ...getDesignPreviewBg(design), color: design.light_text_color || '#e0e7ff' }}>
                                  <div style={{ color: design.bg_type === 'solid' ? (design.light_text_color || '#64748b') : '#fff' }} className="text-xs font-bold opacity-90">Darija Pro</div>
                                  <div className="text-[8px] opacity-50 mb-2">Language Portal</div>
                                  <div className="space-y-0.5">
                                    {['Translation', 'Dictionary', 'Decoder'].map((item, i) => (
                                      <div key={i} className={`text-[8px] px-1.5 py-0.5 ${design.active_style === 'full-width' ? 'rounded-sm' : 'rounded'} ${design.active_style === 'left-border' && i === 0 ? 'border-l-2' : ''}`}
                                        style={i === 0 ? { backgroundColor: design.active_bg_light || '#ffffff', color: design.active_text_light || '#2563eb', fontWeight: 700, borderColor: design.active_text_light } : {}}>
                                        {item}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                                <div className={`p-2 text-center ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
                                  <div className="text-sm font-medium">{design.icon} {design.name}</div>
                                  {navLibrary.active_design_id === design.id && <div className="text-[10px] text-blue-500 font-bold">ACTIVE</div>}
                                </div>
                              </div>
                              {/* Hover Actions */}
                              <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                                <button onClick={(e) => { e.stopPropagation(); setEditingNavDesign({ ...design }); }}
                                  className="p-1 bg-blue-600 text-white rounded text-[10px] hover:bg-blue-700" title="Edit">✏️</button>
                                <button onClick={(e) => { e.stopPropagation(); const dup = { ...design, id: `${design.id}-${Date.now()}`, name: `${design.name} Copy` }; saveNavLibrary({ custom_designs: [...navLibrary.custom_designs, dup] }); }}
                                  className="p-1 bg-green-600 text-white rounded text-[10px] hover:bg-green-700" title="Duplicate">📋</button>
                                <button onClick={(e) => { e.stopPropagation(); if (navLibrary.active_design_id === design.id) { showNotification('Switch to another design first', 'error'); return; } if (selectedNavDesignId === design.id) setSelectedNavDesignId(navLibrary.active_design_id); saveNavLibrary({ custom_designs: navLibrary.custom_designs.filter(d => d.id !== design.id) }); }}
                                  className="p-1 bg-red-600 text-white rounded text-[10px] hover:bg-red-700" title="Delete">🗑️</button>
                              </div>
                            </div>
                          ))}

                          {/* New Design Card */}
                          <div onClick={() => { const newId = `custom-${Date.now()}`; setEditingNavDesign({ ...NAV_TEMPLATE, id: newId, name: 'New Design', icon: '✨' }); }}
                            className={`cursor-pointer rounded-xl border-2 border-dashed flex flex-col items-center justify-center h-[calc(8rem+2.5rem)] transition-colors ${isDarkMode ? 'border-gray-600 hover:border-gray-500 text-gray-400' : 'border-gray-300 hover:border-gray-400 text-gray-500'}`}>
                            <span className="text-2xl mb-1">+</span>
                            <span className="text-xs font-medium">New Design</span>
                          </div>
                        </div>

                        {/* Icon Size Settings (all values in px) */}
                        <div className={`p-4 rounded-xl mb-4 ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                          <h4 className={`text-sm font-bold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Icon Size Settings (px)</h4>
                          <div className="flex items-center gap-3 flex-wrap">
                            <span className={`text-xs font-medium w-32 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Expanded (menu open):</span>
                            <div className="flex items-center gap-1">
                              <input type="number" min="8" max="64" value={navLibrary.nav_icon_size || ''} placeholder="24"
                                onChange={e => setNavLibrary(prev => ({ ...prev, nav_icon_size: e.target.value }))}
                                className={`w-16 px-2 py-1 rounded text-sm border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`} />
                              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>px</span>
                            </div>
                            <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Default: 24px</span>
                          </div>
                          <div className="flex items-center gap-3 flex-wrap mt-2">
                            <span className={`text-xs font-medium w-32 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Collapsed (icons only):</span>
                            <div className="flex items-center gap-1">
                              <input type="number" min="8" max="64" value={navLibrary.nav_icon_size_collapsed || ''} placeholder="14"
                                onChange={e => setNavLibrary(prev => ({ ...prev, nav_icon_size_collapsed: e.target.value }))}
                                className={`w-16 px-2 py-1 rounded text-sm border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200'}`} />
                              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>px</span>
                            </div>
                            <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Default: 14px</span>
                          </div>
                          <div className="mt-3 flex items-center gap-3">
                            <button onClick={() => saveNavLibrary({ nav_icon_size: navLibrary.nav_icon_size || '', nav_icon_size_collapsed: navLibrary.nav_icon_size_collapsed || '' })}
                              className="px-3 py-1 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700">Save Icon Sizes</button>
                            {(navLibrary.nav_icon_size || navLibrary.nav_icon_size_collapsed) && (
                              <button onClick={() => { setNavLibrary(prev => ({ ...prev, nav_icon_size: '', nav_icon_size_collapsed: '' })); saveNavLibrary({ nav_icon_size: '', nav_icon_size_collapsed: '' }); }}
                                className="px-3 py-1 bg-gray-500 text-white rounded text-xs font-medium hover:bg-gray-600">Reset to Defaults</button>
                            )}
                            <span className={`text-[10px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Per-item size overrides these</span>
                          </div>
                        </div>

                        {/* Menu Items Editor */}
                        <div className={`p-5 rounded-xl ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
                          <div className={`p-3 rounded-lg mb-4 text-xs ${isDarkMode ? 'bg-blue-900/30 border border-blue-700 text-blue-300' : 'bg-blue-50 border border-blue-200 text-blue-700'}`}>
                            💡 <strong>To rename menu labels</strong> (e.g. change "My Morocco" → "Travel Tools"), edit them in <strong>Admin → Language Management</strong>. Editing labels here only affects icons &amp; order — text labels come from the translation system so they stay translated for all UI languages.
                          </div>
                          <div className="flex items-center justify-between mb-4">
                            <h4 className={`text-base font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Menu Items</h4>
                            <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Click to edit icon, drag to reorder</span>
                          </div>
                          <div className="space-y-1">
                            {getEffectiveMenuOrder().map((itemId, idx) => {
                              const info = getMenuItem(itemId);
                              const isEditing = editingMenuItem === itemId;
                              const hasSubmenu = !!SUBMENU_DEFAULTS[itemId];
                              const isSubmenuExpanded = expandedSubmenuParent === itemId;
                              return (
                                <div key={itemId}>
                                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                                    {isEditing ? (() => {
                                      const curIcon = navLibrary.menu_items[itemId]?.icon ?? MENU_ITEM_DEFAULTS[itemId]?.icon ?? '';
                                      const curLabel = navLibrary.menu_items[itemId]?.label ?? MENU_ITEM_DEFAULTS[itemId]?.label ?? '';
                                      const curIconUrl = navLibrary.menu_items[itemId]?.icon_url;
                                      return (
                                      <>
                                        {curIconUrl ? (
                                          <div className="flex items-center gap-1">
                                            <img src={`${API.replace('/api', '')}${curIconUrl}`} alt="" className="w-6 h-6 object-contain" />
                                            <button onClick={() => clearIconUrl('menu', itemId)} className="text-red-400 text-xs hover:text-red-300" title="Remove custom icon">✕</button>
                                          </div>
                                        ) : (
                                          <input type="text" value={curIcon} placeholder="Icon"
                                            onChange={e => { const v = e.target.value; setNavLibrary(prev => ({ ...prev, menu_items: { ...prev.menu_items, [itemId]: { ...prev.menu_items[itemId], label: prev.menu_items[itemId]?.label ?? MENU_ITEM_DEFAULTS[itemId]?.label ?? '', icon: v } } })); }}
                                            className={`w-12 px-2 py-1 text-center rounded text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-200'} border`} />
                                        )}
                                        <button onClick={() => openIconPicker({ type: 'menu', itemId })}
                                          className={`px-2 py-1 rounded text-xs ${isDarkMode ? 'bg-purple-700 hover:bg-purple-600 text-white' : 'bg-purple-100 hover:bg-purple-200 text-purple-700'}`}>Pick Icon</button>
                                        <input type="number" min="8" max="64" value={navLibrary.menu_items[itemId]?.icon_size || ''} placeholder="px"
                                          onChange={e => { const v = e.target.value; setNavLibrary(prev => ({ ...prev, menu_items: { ...prev.menu_items, [itemId]: { ...prev.menu_items[itemId], icon_size: v } } })); }}
                                          className={`w-14 px-1 py-1 rounded text-xs ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-200'} border`}
                                          title="Icon size in px" />
                                        <input type="text" value={curLabel} placeholder="Label"
                                          onChange={e => { const v = e.target.value; setNavLibrary(prev => ({ ...prev, menu_items: { ...prev.menu_items, [itemId]: { ...prev.menu_items[itemId], icon: prev.menu_items[itemId]?.icon ?? MENU_ITEM_DEFAULTS[itemId]?.icon ?? '', label: v } } })); }}
                                          className={`flex-1 px-2 py-1 rounded text-sm ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-200'} border`} />
                                        <button onClick={() => { setEditingMenuItem(null); saveNavLibrary({ menu_items: navLibrary.menu_items }); }}
                                          className="px-2 py-1 bg-blue-600 text-white rounded text-xs">Done</button>
                                      </>
                                      );
                                    })() : (
                                      <>
                                        {hasSubmenu && (
                                          <button onClick={() => setExpandedSubmenuParent(isSubmenuExpanded ? null : itemId)}
                                            className={`p-0.5 rounded transition-transform ${isSubmenuExpanded ? 'rotate-90' : ''} ${isDarkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}>
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                                          </button>
                                        )}
                                        <span className="text-lg w-8 text-center">
                                          {navLibrary.menu_items[itemId]?.icon_url
                                            ? <img src={`${API.replace('/api', '')}${navLibrary.menu_items[itemId].icon_url}`} alt="" className="w-5 h-5 object-contain inline" />
                                            : info.icon}
                                        </span>
                                        <span className={`flex-1 text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>{info.label}</span>
                                        {hasSubmenu && <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-500'}`}>{getSubmenuOrder(itemId).length} sub</span>}
                                        <button onClick={() => {
                                          if (!navLibrary.menu_items[itemId]) {
                                            setNavLibrary(prev => ({ ...prev, menu_items: { ...prev.menu_items, [itemId]: { label: MENU_ITEM_DEFAULTS[itemId]?.label || '', icon: MENU_ITEM_DEFAULTS[itemId]?.icon || '' } } }));
                                          }
                                          setEditingMenuItem(itemId);
                                        }} className={`p-1 rounded text-xs ${isDarkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}>✏️</button>
                                        <div className="flex gap-0.5">
                                          <button onClick={() => moveMenuItem(idx, -1)} disabled={idx === 0}
                                            className={`p-0.5 rounded ${idx === 0 ? 'opacity-20' : isDarkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}>
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                                          </button>
                                          <button onClick={() => moveMenuItem(idx, 1)} disabled={idx === getEffectiveMenuOrder().length - 1}
                                            className={`p-0.5 rounded ${idx === getEffectiveMenuOrder().length - 1 ? 'opacity-20' : isDarkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}>
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                          </button>
                                        </div>
                                      </>
                                    )}
                                  </div>
                                  {/* Submenu Items Editor (expanded) */}
                                  {hasSubmenu && isSubmenuExpanded && (
                                    <div className={`ml-6 mt-1 mb-2 p-3 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-blue-50/50 border-blue-200'}`}>
                                      <div className="flex items-center justify-between mb-2">
                                        <span className={`text-xs font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Submenu Items</span>
                                      </div>
                                      <div className="space-y-1">
                                        {getSubmenuOrder(itemId).map((subId, subIdx) => {
                                          const subInfo = getSubMenuItem(itemId, subId);
                                          const isSubEditing = editingSubMenuItem === `${itemId}:${subId}`;
                                          const subCount = getSubmenuOrder(itemId).length;
                                          return (
                                            <div key={subId} className={`flex items-center gap-2 px-2 py-1.5 rounded ${isDarkMode ? 'bg-gray-700/70' : 'bg-white'}`}>
                                              {isSubEditing ? (() => {
                                                const curSubIcon = navLibrary.submenu_items?.[itemId]?.items?.[subId]?.icon ?? (SUBMENU_DEFAULTS[itemId]?.find(d => d.id === subId)?.icon ?? '');
                                                const curSubLabel = navLibrary.submenu_items?.[itemId]?.items?.[subId]?.label ?? (SUBMENU_DEFAULTS[itemId]?.find(d => d.id === subId)?.label ?? '');
                                                const curSubIconUrl = navLibrary.submenu_items?.[itemId]?.items?.[subId]?.icon_url;
                                                return (
                                                <>
                                                  {curSubIconUrl ? (
                                                    <div className="flex items-center gap-1">
                                                      <img src={`${API.replace('/api', '')}${curSubIconUrl}`} alt="" className="w-5 h-5 object-contain" />
                                                      <button onClick={() => clearIconUrl('submenu', subId, itemId)} className="text-red-400 text-[10px] hover:text-red-300">✕</button>
                                                    </div>
                                                  ) : (
                                                    <input type="text" value={curSubIcon} placeholder="Icon"
                                                      onChange={e => { const v = e.target.value; setNavLibrary(prev => {
                                                        const parent = prev.submenu_items?.[itemId] || {};
                                                        const items = { ...parent.items, [subId]: { ...parent.items?.[subId], label: parent.items?.[subId]?.label ?? (SUBMENU_DEFAULTS[itemId]?.find(d => d.id === subId)?.label ?? ''), icon: v } };
                                                        return { ...prev, submenu_items: { ...prev.submenu_items, [itemId]: { ...parent, items } } };
                                                      }); }}
                                                      className={`w-10 px-1 py-1 text-center rounded text-xs ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-200'} border`} />
                                                  )}
                                                  <button onClick={() => openIconPicker({ type: 'submenu', itemId: subId, parentId: itemId })}
                                                    className={`px-1.5 py-0.5 rounded text-[10px] ${isDarkMode ? 'bg-purple-700 hover:bg-purple-600 text-white' : 'bg-purple-100 hover:bg-purple-200 text-purple-700'}`}>Pick</button>
                                                  <input type="text" value={curSubLabel} placeholder="Label"
                                                    onChange={e => { const v = e.target.value; setNavLibrary(prev => {
                                                      const parent = prev.submenu_items?.[itemId] || {};
                                                      const items = { ...parent.items, [subId]: { ...parent.items?.[subId], icon: parent.items?.[subId]?.icon ?? (SUBMENU_DEFAULTS[itemId]?.find(d => d.id === subId)?.icon ?? ''), label: v } };
                                                      return { ...prev, submenu_items: { ...prev.submenu_items, [itemId]: { ...parent, items } } };
                                                    }); }}
                                                    className={`flex-1 px-1 py-1 rounded text-xs ${isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'bg-white border-gray-200'} border`} />
                                                  <button onClick={() => { setEditingSubMenuItem(null); saveNavLibrary({ submenu_items: navLibrary.submenu_items }); }}
                                                    className="px-2 py-0.5 bg-blue-600 text-white rounded text-[10px]">Done</button>
                                                </>
                                                );
                                              })() : (
                                                <>
                                                  <span className="text-sm w-6 text-center">
                                                    {navLibrary.submenu_items?.[itemId]?.items?.[subId]?.icon_url
                                                      ? <img src={`${API.replace('/api', '')}${navLibrary.submenu_items[itemId].items[subId].icon_url}`} alt="" className="w-4 h-4 object-contain inline" />
                                                      : subInfo.icon}
                                                  </span>
                                                  <span className={`flex-1 text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{subInfo.label}</span>
                                                  <button onClick={() => setEditingSubMenuItem(`${itemId}:${subId}`)}
                                                    className={`p-0.5 rounded text-[10px] ${isDarkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}>✏️</button>
                                                  <div className="flex gap-0.5">
                                                    <button onClick={() => moveSubMenuItem(itemId, subIdx, -1)} disabled={subIdx === 0}
                                                      className={`p-0.5 rounded ${subIdx === 0 ? 'opacity-20' : isDarkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}>
                                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                                                    </button>
                                                    <button onClick={() => moveSubMenuItem(itemId, subIdx, 1)} disabled={subIdx === subCount - 1}
                                                      className={`p-0.5 rounded ${subIdx === subCount - 1 ? 'opacity-20' : isDarkMode ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}>
                                                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                                                    </button>
                                                  </div>
                                                </>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                          <button onClick={() => saveNavLibrary({ menu_order: getEffectiveMenuOrder(), submenu_items: navLibrary.submenu_items })}
                            className="mt-3 px-4 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                            Save Order & Labels
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                  {/* Icon Picker Modal */}
                  {iconPickerTarget && (
                    <IconPicker
                      API={API}
                      adminToken={adminToken}
                      isDarkMode={isDarkMode}
                      onClose={() => setIconPickerTarget(null)}
                      onPick={(url) => selectIcon(url)}
                    />
                  )}

                {/* Image Picker Modal */}
      {showImagePicker && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowImagePicker(false)}>
          <div className={`relative w-full max-w-2xl max-h-[80vh] rounded-2xl shadow-2xl flex flex-col ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className={`flex items-center justify-between p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h3 className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>Pick from Image Manager</h3>
              <button onClick={() => setShowImagePicker(false)} className={`p-1 rounded-lg hover:bg-gray-200 ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* Tabs */}
            <div className={`flex gap-1 p-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              {[
                { id: 'icons', label: 'Icons' },
                { id: 'avatars', label: 'Avatars' },
                { id: 'folders', label: 'Folders' }
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setPickerTab(tab.id);
                    setPickerSelectedFolder(null);
                    loadPickerImages(tab.id);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    pickerTab === tab.id
                      ? 'bg-blue-600 text-white'
                      : isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
              {pickerSelectedFolder && (
                <button
                  onClick={() => { setPickerSelectedFolder(null); loadPickerImages('folders'); }}
                  className={`ml-2 px-3 py-2 rounded-lg text-xs ${isDarkMode ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
                >
                  &larr; Back to folders
                </button>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {pickerLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
                </div>
              ) : pickerTab === 'folders' && !pickerSelectedFolder ? (
                /* Folder list */
                <div className="grid grid-cols-3 gap-3">
                  {pickerFolders.length === 0 ? (
                    <p className={`col-span-3 text-center py-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No folders found</p>
                  ) : pickerFolders.map(folder => (
                    <button
                      key={folder.id}
                      onClick={() => {
                        setPickerSelectedFolder(folder);
                        loadPickerImages('folders', folder.id);
                      }}
                      className={`p-4 rounded-xl border text-center transition-colors hover:border-blue-500 ${
                        isDarkMode ? 'border-gray-700 bg-gray-900 text-white' : 'border-gray-200 bg-gray-50 text-gray-800'
                      }`}
                    >
                      <div className="text-2xl mb-1">{folder.icon || '📁'}</div>
                      <div className="text-sm font-medium truncate">{folder.name}</div>
                      <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>{folder.asset_count || 0} images</div>
                    </button>
                  ))}
                </div>
              ) : (
                /* Image grid */
                <div className="grid grid-cols-4 gap-3">
                  {pickerImages.length === 0 ? (
                    <p className={`col-span-4 text-center py-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>No images found</p>
                  ) : pickerImages.map(img => (
                    <button
                      key={img.id || img.url}
                      onClick={() => {
                        const oldUrl = editingDesign?.bg_image_url;
                        if (oldUrl && oldUrl.includes('/login-designs/')) {
                          fetch(`${API}/admin/website-designs/login/delete-image`, {
                            method: 'DELETE', headers: {...authHeaders, 'Content-Type': 'application/json'},
                            body: JSON.stringify({ url: oldUrl })
                          }).catch(() => {});
                        }
                        const fullUrl = img.url?.startsWith('http') ? img.url : `${API.replace('/api', '')}${img.url}`;
                        setEditingDesign(prev => ({...prev, bg_image_url: fullUrl}));
                        setShowImagePicker(false);
                        showNotification('Image selected', 'success');
                      }}
                      className={`group relative aspect-square rounded-xl border overflow-hidden transition-all hover:border-blue-500 hover:shadow-lg ${
                        isDarkMode ? 'border-gray-700 bg-gray-900' : 'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <img
                        src={img.url?.startsWith('http') ? img.url : `${API.replace('/api', '')}${img.url}`}
                        alt={img.name || ''}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-end">
                        <div className="w-full p-1.5 bg-black/60 text-white text-xs truncate opacity-0 group-hover:opacity-100 transition-opacity">
                          {img.name || 'Untitled'}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
                </div>
  );
};

export default WebsiteDesignsAdmin;
